import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../contexts/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import {
  Trophy, ChartBar, Users, Target, Brain, CaretLeft, FilePdf,
  Clock, ShieldWarning, VideoCamera
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AssessmentResults() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [questionAnalytics, setQuestionAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [resData, qaData] = await Promise.all([
          axios.get(`${API}/assessments/${id}/results`, { withCredentials: true }),
          axios.get(`${API}/assessments/${id}/question-analytics`, { withCredentials: true }),
        ]);
        setData(resData.data);
        setQuestionAnalytics(qaData.data);
      } catch {
        toast.error("Failed to load results");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const downloadPdf = async (resultId, email) => {
    setExportingPdf(resultId);
    try {
      const res = await axios.get(
        `${API}/assessments/${id}/results/${resultId}/report-pdf`,
        { withCredentials: true, responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${email.replace("@", "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("PDF export failed");
    } finally {
      setExportingPdf(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-14 flex items-center justify-center" data-testid="results-loading">
        <div className="w-8 h-8 border-2 border-[#FF3366] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { assessment, results, invitations, analytics } = data;
  const qa = questionAnalytics?.questions || [];

  const scoreDistribution = [
    { range: "0-20", count: results.filter(r => r.score < 20).length },
    { range: "20-40", count: results.filter(r => r.score >= 20 && r.score < 40).length },
    { range: "40-60", count: results.filter(r => r.score >= 40 && r.score < 60).length },
    { range: "60-80", count: results.filter(r => r.score >= 60 && r.score < 80).length },
    { range: "80-100", count: results.filter(r => r.score >= 80).length },
  ];

  const statusData = [
    { name: "Completed", value: analytics.total_completed, color: "#10B981" },
    { name: "In Progress", value: analytics.total_in_progress, color: "#F59E0B" },
    { name: "Not Started", value: analytics.total_not_started, color: "#52525B" },
  ].filter(d => d.value > 0);

  const skillMap = {};
  results.forEach(r => {
    (r.skill_breakdown || []).forEach(sb => {
      if (!skillMap[sb.skill]) skillMap[sb.skill] = { correct: 0, total: 0 };
      skillMap[sb.skill].correct += sb.correct;
      skillMap[sb.skill].total += sb.total;
    });
  });
  const skillData = Object.entries(skillMap).map(([skill, d]) => ({
    skill, percentage: Math.round(d.correct / Math.max(d.total, 1) * 100)
  }));

  const formatTime = (sec) => {
    if (sec == null) return "\u2014";
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const diffColor = (d) => {
    if (d === "easy") return "bg-[#10B981]/10 text-[#10B981]";
    if (d === "hard") return "bg-[#EF4444]/10 text-[#EF4444]";
    return "bg-[#F59E0B]/10 text-[#F59E0B]";
  };

  return (
    <div className="min-h-screen pt-14 bg-white" data-testid="results-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-xs text-[#52525B] hover:text-[#0A0A0A] mb-2" data-testid="back-to-dashboard">
              <CaretLeft size={14} /> Dashboard
            </button>
            <h1 className="font-heading font-bold text-3xl tracking-tight text-[#0A0A0A]">
              {assessment.title}
            </h1>
            <p className="text-sm text-[#52525B] mt-1">Results & Analytics</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/assessments/${id}/settings`)}
              className="rounded-none border-black/20 px-4 py-2.5 h-auto text-sm"
              data-testid="settings-link"
            >
              Settings
            </Button>
            <Button
              onClick={() => navigate(`/assessments/${id}/invite`)}
              className="bg-[#0A0A0A] text-white hover:bg-black rounded-none px-5 py-2.5 h-auto text-sm font-semibold"
              data-testid="invite-more-btn"
            >
              <Users size={16} className="mr-1.5" /> Invite More
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-black/10 mb-8" data-testid="results-stats">
          {[
            { label: "Invited", value: analytics.total_invited, icon: Users },
            { label: "Completed", value: analytics.total_completed, icon: ChartBar },
            { label: "Avg Score", value: `${analytics.average_score}%`, icon: Target },
            { label: "Pass Rate", value: `${analytics.pass_rate}%`, icon: Trophy },
            { label: "Highest", value: `${analytics.highest_score}%`, icon: Brain },
          ].map((s, i) => (
            <div key={i} className="p-4 border-r last:border-r-0 border-black/10">
              <div className="flex items-center gap-1.5 mb-1.5">
                <s.icon size={14} className="text-[#52525B]" />
                <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">{s.label}</span>
              </div>
              <p className="font-heading font-black text-xl text-[#0A0A0A] tabular-nums">{s.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="leaderboard" className="w-full">
          <TabsList className="rounded-none border border-black/10 bg-[#F8F9FA] h-auto p-0 flex-wrap">
            {["leaderboard", "questions", "charts", "skills"].map(t => (
              <TabsTrigger key={t} value={t}
                className="rounded-none px-5 py-2.5 text-xs font-semibold uppercase tracking-wider data-[state=active]:bg-white data-[state=active]:text-[#0A0A0A] data-[state=active]:shadow-none text-[#52525B]"
                data-testid={`tab-${t}`}>
                {t}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="leaderboard" className="mt-4">
            {results.length === 0 ? (
              <div className="border border-black/10 p-12 text-center" data-testid="no-results">
                <ChartBar size={40} className="text-[#52525B]/30 mx-auto mb-3" />
                <p className="text-sm text-[#52525B]">No results yet. Invite candidates to start.</p>
              </div>
            ) : (
              <div className="border border-black/10" data-testid="leaderboard">
                <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-black/10 bg-[#F8F9FA]">
                  <span className="col-span-1 text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">#</span>
                  <span className="col-span-3 text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Candidate</span>
                  <span className="col-span-1 text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B] text-center">Score</span>
                  <span className="col-span-1 text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B] text-center">Result</span>
                  <span className="col-span-1 text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B] text-center">Time</span>
                  <span className="col-span-1 text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B] text-center">Pass</span>
                  <span className="col-span-2 text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B] text-center">Proctoring</span>
                  <span className="col-span-2 text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B] text-right">Actions</span>
                </div>
                {results.map((r, i) => {
                  const proct = r.proctoring_data || {};
                  const hasViolations = (proct.tab_switches || 0) > 0 || (proct.fullscreen_exits || 0) > 0;
                  return (
                    <div key={r.result_id} data-testid={`result-row-${i}`}>
                      <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-black/5 hover:bg-[#F8F9FA] items-center">
                        <span className="col-span-1 text-sm font-heading font-bold text-[#0A0A0A] tabular-nums">
                          {i + 1}
                        </span>
                        <div className="col-span-3">
                          <p className="text-sm font-medium text-[#0A0A0A] truncate">{r.candidate_email}</p>
                        </div>
                        <div className="col-span-1 text-center">
                          <span className={`text-sm font-bold tabular-nums
                            ${r.score >= (analytics.passing_score || 70) ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                            {r.score}%
                          </span>
                        </div>
                        <div className="col-span-1 text-center">
                          <span className="text-xs text-[#0A0A0A] tabular-nums">{r.correct_count}/{r.total_questions}</span>
                        </div>
                        <div className="col-span-1 text-center">
                          <span className="text-xs text-[#52525B] tabular-nums">{formatTime(r.time_taken)}</span>
                        </div>
                        <div className="col-span-1 text-center">
                          <span className={`text-[9px] px-1.5 py-0.5 font-bold
                            ${r.score >= (analytics.passing_score || 70) ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`}>
                            {r.score >= (analytics.passing_score || 70) ? 'PASS' : 'FAIL'}
                          </span>
                        </div>
                        <div className="col-span-2 flex items-center justify-center gap-1 flex-wrap">
                          {proct.tab_switches > 0 && (
                            <span className="text-[8px] px-1.5 py-0.5 bg-[#EF4444]/10 text-[#EF4444] font-bold flex items-center gap-0.5">
                              <ShieldWarning size={10} /> {proct.tab_switches}
                            </span>
                          )}
                          {proct.fullscreen_exits > 0 && (
                            <span className="text-[8px] px-1.5 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B] font-bold">
                              FS:{proct.fullscreen_exits}
                            </span>
                          )}
                          {proct.webcam_enabled && (
                            <span className="text-[8px] px-1.5 py-0.5 bg-[#10B981]/10 text-[#10B981] font-bold flex items-center gap-0.5">
                              <VideoCamera size={10} /> {proct.webcam_snapshots || 0}
                            </span>
                          )}
                          {!hasViolations && !proct.webcam_enabled && (
                            <span className="text-[8px] text-[#52525B]">Clean</span>
                          )}
                        </div>
                        <div className="col-span-2 flex items-center justify-end gap-1">
                          <button
                            onClick={() => downloadPdf(r.result_id, r.candidate_email)}
                            disabled={exportingPdf === r.result_id}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold border border-black/10 hover:bg-black/5 transition-colors"
                            data-testid={`export-pdf-${i}`}
                          >
                            {exportingPdf === r.result_id ? (
                              <span className="w-3 h-3 border border-[#FF3366] border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <FilePdf size={12} className="text-[#FF3366]" />
                            )}
                            PDF
                          </button>
                        </div>
                      </div>
                      {r.ai_recommendation && (
                        <div className="px-5 pb-2 border-b border-black/5">
                          <p className="text-xs text-[#52525B] bg-[#F8F9FA] p-2 border-l-2 border-[#FF3366]">
                            <strong>AI:</strong> {r.ai_recommendation}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="questions" className="mt-4">
            {qa.length === 0 ? (
              <div className="border border-black/10 p-12 text-center">
                <p className="text-sm text-[#52525B]">No question analytics available yet.</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="border border-black/10 p-5" data-testid="question-time-chart">
                  <h3 className="font-heading font-semibold text-sm text-[#0A0A0A] mb-4">Average Time per Question</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={qa.map((q, i) => ({ name: `Q${i + 1}`, time: q.avg_time, accuracy: q.accuracy }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#52525B" />
                      <YAxis tick={{ fontSize: 10 }} stroke="#52525B" label={{ value: "Seconds", angle: -90, position: "insideLeft", style: { fontSize: 10 } }} />
                      <Tooltip contentStyle={{ border: '1px solid #E4E4E7', borderRadius: 0, fontSize: 11 }}
                        formatter={(v, name) => [name === "time" ? `${v}s` : `${v}%`, name === "time" ? "Avg Time" : "Accuracy"]} />
                      <Bar dataKey="time" fill="#0A0A0A" name="Avg Time (s)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="border border-black/10 p-5" data-testid="question-accuracy-chart">
                  <h3 className="font-heading font-semibold text-sm text-[#0A0A0A] mb-4">Accuracy per Question</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={qa.map((q, i) => ({ name: `Q${i + 1}`, accuracy: q.accuracy }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#52525B" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#52525B" />
                      <Tooltip contentStyle={{ border: '1px solid #E4E4E7', borderRadius: 0, fontSize: 11 }}
                        formatter={(v) => `${v}%`} />
                      <Bar dataKey="accuracy" fill="#FF3366" name="Accuracy %" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="border border-black/10" data-testid="question-analytics-table">
                  <div className="px-5 py-3 border-b border-black/10 bg-[#F8F9FA]">
                    <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">
                      Per-Question Breakdown ({questionAnalytics?.total_candidates || 0} candidates)
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full dense-table">
                      <thead>
                        <tr className="bg-[#F8F9FA]">
                          <th className="text-left px-4 py-2 text-[10px] tracking-wider uppercase font-bold text-[#52525B] w-10">#</th>
                          <th className="text-left px-4 py-2 text-[10px] tracking-wider uppercase font-bold text-[#52525B]">Question</th>
                          <th className="text-center px-4 py-2 text-[10px] tracking-wider uppercase font-bold text-[#52525B] w-20">Skill</th>
                          <th className="text-center px-4 py-2 text-[10px] tracking-wider uppercase font-bold text-[#52525B] w-16">Diff</th>
                          <th className="text-center px-4 py-2 text-[10px] tracking-wider uppercase font-bold text-[#52525B] w-20">Accuracy</th>
                          <th className="text-center px-4 py-2 text-[10px] tracking-wider uppercase font-bold text-[#52525B] w-20">Avg Time</th>
                          <th className="text-center px-4 py-2 text-[10px] tracking-wider uppercase font-bold text-[#52525B] w-20">Avg Visits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {qa.map((q, i) => (
                          <tr key={q.question_id} className="hover:bg-[#F8F9FA]" data-testid={`qa-row-${i}`}>
                            <td className="px-4 py-2.5 text-xs font-bold text-[#0A0A0A] tabular-nums">{i + 1}</td>
                            <td className="px-4 py-2.5 text-xs text-[#0A0A0A]" title={q.question_text}>
                              {q.question_text}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className="text-[9px] px-1.5 py-0.5 bg-[#F8F9FA] text-[#52525B] font-medium">
                                {q.skill_tag || "\u2014"}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase ${diffColor(q.difficulty)}`}>
                                {q.difficulty}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <div className="flex flex-col items-center gap-1">
                                <span className={`text-xs font-bold tabular-nums ${q.accuracy >= 70 ? 'text-[#10B981]' : q.accuracy >= 40 ? 'text-[#F59E0B]' : 'text-[#EF4444]'}`}>
                                  {q.accuracy}%
                                </span>
                                <Progress value={q.accuracy} className="h-1 w-12 rounded-none [&>div]:bg-current" />
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className="text-xs text-[#0A0A0A] tabular-nums flex items-center justify-center gap-1">
                                <Clock size={10} className="text-[#52525B]" />
                                {formatTime(q.avg_time)}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className="text-xs text-[#52525B] tabular-nums">{q.avg_visits}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="charts" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-black/10 p-5" data-testid="score-chart">
                <h3 className="font-heading font-semibold text-sm text-[#0A0A0A] mb-4">Score Distribution</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={scoreDistribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="range" tick={{ fontSize: 11 }} stroke="#52525B" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#52525B" allowDecimals={false} />
                    <Tooltip contentStyle={{ border: '1px solid #E4E4E7', borderRadius: 0, fontSize: 12 }} />
                    <Bar dataKey="count" fill="#FF3366" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="border border-black/10 p-5" data-testid="status-chart">
                <h3 className="font-heading font-semibold text-sm text-[#0A0A0A] mb-4">Candidate Status</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={statusData} cx="50%" cy="50%" innerRadius={60} outerRadius={90}
                      paddingAngle={2} dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}`}>
                      {statusData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ border: '1px solid #E4E4E7', borderRadius: 0, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="skills" className="mt-4">
            <div className="border border-black/10 p-5" data-testid="skills-chart">
              <h3 className="font-heading font-semibold text-sm text-[#0A0A0A] mb-4">Average Performance by Skill</h3>
              {skillData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={skillData} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#52525B" />
                    <YAxis type="category" dataKey="skill" tick={{ fontSize: 11 }} stroke="#52525B" width={80} />
                    <Tooltip contentStyle={{ border: '1px solid #E4E4E7', borderRadius: 0, fontSize: 12 }}
                      formatter={(v) => `${v}%`} />
                    <Bar dataKey="percentage" fill="#0A0A0A" barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[#52525B] text-center py-8">No skill data available yet.</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
